import {doppeActionTypeDefineMetadata} from '../../client-server-common/doppe-action-types/doppe-action-type';
import type {HideableValue, OverridableValue} from '@wix/devzai-utils-common';
import {
    arrayFlatten,
    arrayRemoveNullValues,
    arraySkipTake,
    evaluateFunction,
    hideableValueCreateHidden,
    hideableValueGetValue,
    hideableValueIsVisibleAndNotEqualValue,
    immerProduce,
    iterableMapToArray,
    overridableValueGetOverriddenValue,
    overridableValueGetValue,
    stringIsNotNullableOrWhiteSpace
} from '@wix/devzai-utils-common';
import type {DoppeActionTypeBlogPost} from '../doppe-action-type-blog-post/doppe-action-type-blog-post';
import {
    doppeActionTypeBlogPostActionContentSettingsMetadata,
    doppeActionTypeBlogPostResolveSettingsFromBoundEntity,
    populateBlogPostActionSettingsDefaultTexts
} from '../doppe-action-type-blog-post/doppe-action-type-blog-post';
import {wixMediaResourceReplaceExternalImage} from '@wix/devzai-common-wix';
import {
    convertCommonListSettingToDefaultDoppeActionCtaButtonSettingsFromListAction,
    DoppeBlogPostsExternalDataSource,
    doppeBlogPostsExternalDataSourceResolveBusinessEntityReferences,
    DoppeBlogPostsExternalDataSourceType,
    doppeBusinessEntityDtoIsNotFound,
    DoppeDtoBlogPost,
    doppeDtoBlogPostGetMedia,
    DoppeExternalBlogPostProvider,
    doppeExternalBlogPostProviderResolveBusinessEntityReference,
    doppeLinkSpecCreateEntity,
    DoppeLinkSpecEntityType,
    BymoPagePropsSpecialContentSettingMetadata,
    doppeListActionContentSettingsMetadataCreateForListActionWithMainDetails,
    doppeListActionGetDefaultListSettings,
    doppeListActionGetDefaultSearchListSettings,
    doppeListActionGetItemClickBehaviourDefaultListSettings,
    doppeListActionGetMainDetailsDefaultSettings,
    doppeListActionGetWithExternalDataSourceDefaultListSettings,
    doppeListActionItemCtaButtonsResetSettingsForLock,
    doppeListActionListItemCalculateItemButtons,
    doppeListActionListItemSettingsMigrateBaseSettings,
    doppeListActionListItemWithCtaButtonsMigration,
    doppeListActionListItemWithSectionsMigration,
    DoppeListActionPartialSettings,
    DoppeListActionSettings,
    doppeListActionSettingsFilterHiddenListItemsFromActionView,
    doppeListActionSettingsGetListItemCtaButtonsDefaultSettings,
    doppeListActionSettingsGetListItemSectionsDefaultSettings,
    doppeListActionTemplateCtaButtonsSettingsDefaultValuesMigration,
    doppeListActionTemplateSectionsSettingsDefaultValuesMigration,
    doppeListActionWithMainDetailsGetMainImage,
    doppeListActionWithMainDetailsReplaceMainImage,
} from '../../client-server-common';
import {bymoPageContextPropsResolveBusinessEntity, bymoPageContextPropsResolveBusinessEntityListItems} from '../../doppe-sdk';
import {
    doppeDtoActionSettingsRemoveInnerCtaContentForLockedAction
} from '../../client-server-common/types/doppe-dto-action';
import type {DoppeAppPageContentLocales} from '../../client/doppe-app-locales/doppe-app-page-content-locales';
import {doppeActionCtaButtonsResetSettingsForLock} from '../../client-server-common/types/doppe-action-cta-button';
import type {DoppeActionCtaButtonSettings} from '../../client-server-common/types/doppe-action-cta-button-settings';
import {
    doppeActionCtaButtonTypeGetDefaultCtaButtonSettingsMigration,
    getDefaultDoppeActionCtaButtonSettings
} from '../../client-server-common/types/doppe-action-cta-button-settings';
import {
    doppeActionCtaButtonsResolveMainURL
} from '../doppe-actions-common/doppe-cta-button-utils/doppe-cta-button-utils';

import {
    doppeBlogPostActionGetMediaViewDefaultSettings,
    doppeBlogPostsListActionResolveExternalItemSettingsFromDoppeDtoBlogPost
} from '../../doppe-action-types-client-server-common/doppe-blog-post-action-types-utils/doppe-blog-post-action-types-utils';
import type {
    DoppeActionContentSectionsSettings
} from '../../client-server-common/types/doppe-action-content-sections-settings';


import type {DoppeActionCtaButtonsSettings} from '../../client-server-common/types/doppe-action-cta-buttons-settings';


export namespace DoppeActionTypeArticlesList {

    export interface ArticleSettings extends
        DoppeListActionSettings.ListItemWithCtaButtons,
        DoppeListActionSettings.ListItemWithSections,
        DoppeListActionSettings.ListItemBaseSettings,
        DoppeActionTypeBlogPost.ArticleSettings
    {
        blogPostTitle: OverridableValue<string>;
    }

    export interface ActionSettings extends
        DoppeListActionSettings<ArticleSettings>,
        DoppeListActionSettings.WithSearchSettings,
        DoppeListActionSettings.WithMainDetails,
        DoppeListActionSettings.WithItemClickBehaviour,
        DoppeListActionSettings.WithExternalDataSourceSupport<DoppeBlogPostsExternalDataSource>,
        DoppeActionContentSectionsSettings,
        DoppeActionCtaButtonsSettings
    {
        previewShowTitle: boolean;
        previewShowDescription: boolean;
        previewShowBlogPostDate: boolean;
        previewShowBlogWebsite: boolean;
    }

    export interface DeprecatedFields extends Partial<DoppeListActionSettings.WithCtaButton> {
        articles?: ArticleSettings[];
        previewBlogPostButtonText?: string;
    }

    export interface DeprecatedItemFields extends Partial<DoppeActionCtaButtonSettings> {
        blogPostButtonText?: HideableValue<string>;
    }

    export type PartialProps = DoppeListActionPartialSettings<ActionSettings, ArticleSettings, DeprecatedFields, DeprecatedItemFields>;
}


export const doppeActionTypeArticlesList = doppeActionTypeDefineMetadata<DoppeActionTypeArticlesList.ActionSettings, DoppeActionTypeArticlesList.PartialProps>({
    id: 'io.bymo.action/articles-list',
    resolveActionSettings: action => {

        const {
            listDataItems = [],
            ctaButton,
            previewShowBlogPostDate = true,
            previewShowBlogWebsite = true,
            previewShowDescription = true,
            previewShowTitle = true,
            ...restSettings
        } = action.settings;

        return {
            ...restSettings,
            previewShowBlogPostDate: previewShowBlogPostDate,
            previewShowBlogWebsite: previewShowBlogWebsite,
            previewShowDescription: previewShowDescription,
            previewShowTitle: previewShowTitle,
            ...doppeListActionGetWithExternalDataSourceDefaultListSettings(action.settings),
            ...doppeListActionGetDefaultSearchListSettings(action.settings),
            ...doppeListActionGetDefaultListSettings<DoppeActionTypeArticlesList.ArticleSettings>({
                ...action.settings,
                listDataItems: listDataItems.map(article => {

                    const {
                        blogPostButtonText,
                        ctaButton,
                        blogPostTitle = '',
                        blogPostLink = '',
                        description = hideableValueCreateHidden(''),
                        blogPostImages = null,
                        blogPostDate = hideableValueCreateHidden(''),
                        showBlogWebsite = false,
                        blogPostProvider = null,
                        blogPostProviderId = null,
                        ...restArticleSettings
                    } = article;

                    return {
                        ...restArticleSettings,
                        ...doppeListActionListItemSettingsMigrateBaseSettings(article),
                        blogPostTitle: blogPostTitle,
                        blogPostLink: blogPostLink,
                        description: description,
                        blogPostImages: blogPostImages,
                        blogPostDate: blogPostDate,
                        showBlogWebsite: showBlogWebsite,
                        blogPostProvider: blogPostProvider,
                        blogPostProviderId: blogPostProviderId,
                        ...getDefaultDoppeActionCtaButtonSettings({
                            buttonText: blogPostButtonText,
                            showButton: true
                        }),
                        ...doppeActionCtaButtonTypeGetDefaultCtaButtonSettingsMigration({
                            hasSourceURL: true,
                            ctaButton: ctaButton,
                        }),
                        ...doppeListActionListItemWithCtaButtonsMigration(article, true),
                        ...doppeListActionListItemWithSectionsMigration(article),
                        ...doppeBlogPostActionGetMediaViewDefaultSettings({
                            mainMediaViewSettings: article.mainMediaViewSettings,
                            mainMediaViewLayout: article.mainMediaViewLayout,
                        }),
                    };
                }),
            }),
            ...doppeListActionGetItemClickBehaviourDefaultListSettings(action.settings),
            ...doppeListActionGetMainDetailsDefaultSettings({
                defaultTitle: ''
            }, action.settings),
            ...convertCommonListSettingToDefaultDoppeActionCtaButtonSettingsFromListAction(action.settings),
            ...doppeActionCtaButtonTypeGetDefaultCtaButtonSettingsMigration({
                ctaButton: ctaButton,
                hasSourceURL: false
            }),
            ...doppeListActionTemplateCtaButtonsSettingsDefaultValuesMigration(action.settings, true),
            ...doppeListActionTemplateSectionsSettingsDefaultValuesMigration(action.settings),
        } satisfies DoppeActionTypeArticlesList.ActionSettings;
    },
    actionSettingsMetadata: {
        contentSettingsMetadata: {
            ...doppeListActionContentSettingsMetadataCreateForListActionWithMainDetails(),
            externalDataSourceItemsCount: BymoPagePropsSpecialContentSettingMetadata.Ignored,
        },
        supportsActionCtaButtons: {
            customCtaButtonTypes: {}
        },
        supportsContentSections: {
            customContentSectionsTypes: {}
        },
        hideableContentSettingsNames: {
                mainImage: true,
                mainTitle: true
            }
    },
    actionListItemSettingsMetadata: {
        contentSettingsMetadata: doppeActionTypeBlogPostActionContentSettingsMetadata,
        supportsActionCtaButtons: {
            customCtaButtonTypes: {}
        },
        supportsContentSections: {
            customContentSectionsTypes: {}
        },
        hideableContentSettingsNames: {
                blogPostDate: true,
                description: true
            }
    },
    isWidgetOpener: true,
    canRenderInPage: true,
    skipReportingOnGoalAchievedOnActionButtonClick: true,
    isListItemLinkOpener: true,
    supportsListLayout: true,
    prepareForViewer: (actionSettings, options) => {
        doppeDtoActionSettingsRemoveInnerCtaContentForLockedAction(actionSettings, actionSettings => {
            doppeListActionItemCtaButtonsResetSettingsForLock(actionSettings);
            doppeActionCtaButtonsResetSettingsForLock(actionSettings);
        }, options);
    },
    resolveUsedMedia: actionSettings => {
        return arrayRemoveNullValues([...arrayFlatten(actionSettings.listDataItems.map(article => {
            return overridableValueGetValue(article.blogPostImages) ?? []
        })), hideableValueIsVisibleAndNotEqualValue(actionSettings.mainImage, null) ? actionSettings.mainImage : null]);
    },
    resolveMainMedia: (actionSettings, bymoPageContextProps) => {
        const firstItem = actionSettings.listDataItems[0];
        const firstItemBlogPost = firstItem?.blogPostProvider !== null && firstItem?.blogPostProviderId !== null ? (() => {
            const entityDto = bymoPageContextPropsResolveBusinessEntity(
                bymoPageContextProps,
                doppeExternalBlogPostProviderResolveBusinessEntityReference(firstItem.blogPostProvider!, firstItem.blogPostProviderId!)
            );
            return entityDto && !doppeBusinessEntityDtoIsNotFound(entityDto) ? entityDto as DoppeDtoBlogPost : null;
        })() : null;

        const firstItemImages = overridableValueGetOverriddenValue(
            firstItem?.blogPostImages ?? null,
            firstItemBlogPost ? doppeDtoBlogPostGetMedia(firstItemBlogPost) : null
        );
        return doppeListActionWithMainDetailsGetMainImage(actionSettings, firstItemImages?.[0] ?? null);
    },
    resolveMainLink: (actionSettings, viewerLang, bymoPageProps, viewerPagesRoutesManager, bymoPageContextProps) => {
        return doppeActionCtaButtonsResolveMainURL(actionSettings, {
            sourceLinkSpec: null,
            viewerLang: viewerLang,
            bymoPageProps: bymoPageProps,
            viewerPagesRoutesManager: viewerPagesRoutesManager,
            bymoPageContextProps: bymoPageContextProps
        }) ?? null;
    },
    isConnectedToExternalDataSource: actionSettings => {
        return !!actionSettings.externalDataSource
    },
    resolveActualActionSettings: (actionSettings, bymoPageContextProps) => {
        const {externalDataSource, externalDataSourceItemsCount, itemClickBehaviour} = actionSettings;
        if (externalDataSource !== null) {
            const businessEntityRef = doppeBlogPostsExternalDataSourceResolveBusinessEntityReferences(externalDataSource);
            const externalArticles = bymoPageContextPropsResolveBusinessEntityListItems(bymoPageContextProps, businessEntityRef);

            const listDataItems = arraySkipTake(externalArticles, 0, externalDataSourceItemsCount).map(doppeDtoBlogPost => {
                return doppeBlogPostsListActionResolveExternalItemSettingsFromDoppeDtoBlogPost({
                    blogPost: doppeDtoBlogPost,
                    listItemClickBehaviour: itemClickBehaviour,
                    blogPostProvider: externalDataSource.type === DoppeBlogPostsExternalDataSourceType.LatestHoppBlogPosts ||
                        externalDataSource.type === DoppeBlogPostsExternalDataSourceType.HoppBlogPostsByCategories
                        ? DoppeExternalBlogPostProvider.Hopp
                        : DoppeExternalBlogPostProvider.Wix
                });
            });

            return {
                ...actionSettings,
                listDataItems: listDataItems
            };
        }
        return actionSettings;
    },
    resolveActualListItemSettings: (listItemId, actionSettings, bymoPageContextProps) => {
        const listItem = actionSettings.listDataItems.find(item => item.id === listItemId);
        if (!listItem) return undefined;

        const {blogPostProvider, blogPostProviderId} = listItem;
        if (blogPostProvider === null || blogPostProviderId === null) return listItem;

        const boundBlogPost = bymoPageContextPropsResolveBusinessEntity(
            bymoPageContextProps,
            doppeExternalBlogPostProviderResolveBusinessEntityReference(blogPostProvider, blogPostProviderId)
        );

        if (!boundBlogPost || doppeBusinessEntityDtoIsNotFound(boundBlogPost)) return undefined;

        return {
            ...listItem,
            ...doppeActionTypeBlogPostResolveSettingsFromBoundEntity(listItem, boundBlogPost as DoppeDtoBlogPost),
        } as DoppeActionTypeArticlesList.ArticleSettings;
    },
    resolveUsedBusinessEntities: actionSettings => {
        const externalDataSource = actionSettings.externalDataSource;
        if (externalDataSource) {
            return [
                doppeBlogPostsExternalDataSourceResolveBusinessEntityReferences(externalDataSource)
            ];
        } else {
            return iterableMapToArray(actionSettings.listDataItems, (article, skip) => {
                return article.blogPostProvider !== null && article.blogPostProviderId !== null ?
                    doppeExternalBlogPostProviderResolveBusinessEntityReference(
                        article.blogPostProvider,
                        article.blogPostProviderId
                    ) : skip
            });
        }
    },
    resolveListItemsCount: actionSettings => {
        return actionSettings.externalDataSource ? actionSettings.externalDataSourceItemsCount : doppeListActionSettingsFilterHiddenListItemsFromActionView(actionSettings.listDataItems).length;
    },
    resolveListItems: (action, viewerLang, bymoPageProps, viewerPagesRoutesManager, bymoPageContextProps) => {
        const actionSettings = action.settings;
        return actionSettings.listDataItems.map(item => {
            const boundBlogPost = item.blogPostProvider !== null && item.blogPostProviderId !== null ? (() => {
                const entityDto = bymoPageContextPropsResolveBusinessEntity(
                    bymoPageContextProps,
                    doppeExternalBlogPostProviderResolveBusinessEntityReference(item.blogPostProvider!, item.blogPostProviderId!)
                );
                return entityDto && !doppeBusinessEntityDtoIsNotFound(entityDto) ? entityDto as DoppeDtoBlogPost : null;
            })() : null;

            const resolvedImages = overridableValueGetOverriddenValue(
                item.blogPostImages,
                boundBlogPost ? doppeDtoBlogPostGetMedia(boundBlogPost) : null
            );

            const sourceLinkSpec = evaluateFunction(() => {
                if (item.blogPostProvider === DoppeExternalBlogPostProvider.Hopp && item.blogPostProviderId !== null) {
                    return doppeLinkSpecCreateEntity(DoppeLinkSpecEntityType.BlogPost, item.blogPostProviderId);
                }
                return stringIsNotNullableOrWhiteSpace(item.blogPostLink) ? item.blogPostLink : null;
            });

            return {
                url: doppeActionCtaButtonsResolveMainURL(
                    doppeListActionListItemCalculateItemButtons(actionSettings.listItemTemplateCtaButtonsSettings, item),
                    {
                        sourceLinkSpec: sourceLinkSpec,
                        viewerLang: viewerLang,
                        bymoPageProps: bymoPageProps,
                        viewerPagesRoutesManager: viewerPagesRoutesManager,
                        bymoPageContextProps: bymoPageContextProps
                    }
                ) ?? null,
                title: overridableValueGetOverriddenValue(item.blogPostTitle, boundBlogPost?.title ?? ''),
                action: action,
                couponCode: hideableValueCreateHidden(''),
                description: item.description,
                image: resolvedImages?.[0] ?? null,
                itemId: item.id,
                ribbon: hideableValueCreateHidden(''),
                enabled: item.enabled,
                deleted: item.deleted,
                displayInSearch: item.displayInSearch,
            }
        });
    },
    resolveListSearchSettings: actionSettings => {
        return actionSettings.listSearchSettings;
    },
    replaceExternalImages: (actionSettings, externalImagesUrlToImageResourceMap) => {
        return immerProduce(actionSettings, actionSettings => {
            for (const article of actionSettings.listDataItems) {
                const images = overridableValueGetValue(article.blogPostImages);
                if (images) {
                    article.blogPostImages = images.map(
                        image => wixMediaResourceReplaceExternalImage(image, externalImagesUrlToImageResourceMap));
                }
            }

            doppeListActionWithMainDetailsReplaceMainImage(actionSettings, externalImagesUrlToImageResourceMap);
        })
    },
});

export function populateBlogPostsActionPostSettingsDefaultTexts(options: {
    blogPost: DoppeActionTypeArticlesList.ArticleSettings,
    pageContentLocales: DoppeAppPageContentLocales,
}) : DoppeActionTypeArticlesList.ArticleSettings {

    const {
        pageContentLocales,
        blogPost,
    } = options;

    const normalizedBlogPost = populateBlogPostActionSettingsDefaultTexts({
        pageContentLocales: pageContentLocales,
        blogPost: blogPost
    })

    return {
        ...blogPost,
        ...normalizedBlogPost,
        blogPostTitle: hideableValueGetValue(normalizedBlogPost.blogPostTitle),
        ...doppeListActionSettingsGetListItemCtaButtonsDefaultSettings(),
        ...doppeListActionSettingsGetListItemSectionsDefaultSettings(),
    }
}

